import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import {
  PLAuthError,
  PlAuthService,
  PLAuthSuccess,
  PlImpersonationService,
  selectAuthState,
} from 'pl-core-services';
import { environment } from '@environments/environment';
import { BehaviorSubject, filter, firstValueFrom, timeout } from 'rxjs';
import { take } from 'rxjs/operators';

interface RefreshChannelMessage extends AuthChannelMessageBase {
  type: 'REFRESH_INIT' | 'REFRESH_DONE';
  token?: string;
  oidcState?: string;
  userData?: any;
}

interface AuthChannelMessageBase {
  type:
    | 'REQUEST_AUTH_STATE'
    | 'AUTH_STATE'
    | 'REFRESH_INIT'
    | 'REFRESH_DONE'
    | 'LOGOUT_INIT'
    | 'TAB_ID_ANNOUNCEMENT'
    | 'TAB_ID_CONFLICT';
  tabId: string;
  timestamp: number;
}

interface LogoutMessage extends AuthChannelMessageBase {
  type: 'LOGOUT_INIT';
}

interface RequestAuthStateMessage extends AuthChannelMessageBase {
  type: 'REQUEST_AUTH_STATE';
}

interface AuthStateMessage extends AuthChannelMessageBase {
  type: 'AUTH_STATE';
  token: string;
  userData: any;
  oidcState: string;
}

interface TabIdAnnouncementMessage extends AuthChannelMessageBase {
  type: 'TAB_ID_ANNOUNCEMENT';
}

interface TabIdConflictMessage extends AuthChannelMessageBase {
  type: 'TAB_ID_CONFLICT';
  conflictingTabId: string;
}

interface UserData {
  auth_db_user_uuid: string;
  auth_db_user_username: string;
  email: string;
  email_verified: boolean;
  family_name: string;
  given_name: string;
  name: string;
  preferred_username: string;
  role: string;
  sub: string;
}

interface OIDCState {
  access_token_expires_at: number | null;
  authNonce: string | null;
  authStateControl: string | null;
  authWellKnownEndPoints: any;
  authnResult: any;
  authzData: string;
  codeVerifier: string;
  jwtKeys: any;
  session_state: string;
  storageSilentRenewRunning: string;
  userData: UserData;
}

type AuthChannelMessage =
  | RequestAuthStateMessage
  | AuthStateMessage
  | RefreshChannelMessage
  | LogoutMessage
  | TabIdAnnouncementMessage
  | TabIdConflictMessage;

const LOG_ERROR = 'ERROR';
const LOG_WARN = 'WARN';
const LOG_INFO = 'INFO';

// App namespace loaded from /assets/js/auth-constants.js
// Must match value in index.html tab ID protection script
const APP_NAMESPACE = (window as any).AUTH_CONSTANTS?.APP_NAMESPACE || 'te';

// Key for persisting the logout timestamp across page reloads
// CRITICAL: This prevents auto-login after logout by blocking checkAuth()
const RECENTLY_LOGGED_OUT_KEY = `${APP_NAMESPACE}_recently_logged_out`;

// A stored logout timestamp only counts as "recent" inside this window
const RECENT_LOGOUT_WINDOW_MS = 5000;

@Injectable({ providedIn: 'root' })
export class AuthSyncService implements OnDestroy {
  private authChannel: BroadcastChannel;

  private leaderKey = `${APP_NAMESPACE}_leader_key`;

  private isLeader = false;

  private authWorker: Worker | null = null;

  private authStateReceived$ = new BehaviorSubject<boolean>(false);

  // Flag to prevent race conditions during logout
  private isLoggingOut = false;

  // Tab ID cache - survives sessionStorage clearing
  private tabIdCache: string | null = null;

  // Passive wait timeout - cleared when leader returns or service destroyed
  private passiveWaitTimeout: any = null;

  // Session polling interval for proactive logout discovery
  private sessionPollingInterval: ReturnType<typeof setInterval> | null = null;

  // Session polling configuration (30 seconds)
  private readonly SESSION_POLL_INTERVAL_MS = 30000;

  async waitForAuthState(timeoutMs: number = 2000): Promise<boolean> {
    try {
      await firstValueFrom(
        this.authStateReceived$.pipe(
          filter((received) => received),
          take(1),
          timeout(timeoutMs)
        )
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if user logged out within the last RECENT_LOGOUT_WINDOW_MS. The
   * timestamp persists across page reloads.
   * Used to prevent checkAuth() from silently re-authenticating after logout.
   */
  isRecentlyLoggedOut(): boolean {
    const loggedOutAt = Number(localStorage.getItem(RECENTLY_LOGGED_OUT_KEY));
    if (!loggedOutAt) {
      return false;
    }
    return Date.now() - loggedOutAt < RECENT_LOGOUT_WINDOW_MS;
  }

  /**
   * Check if a valid Sirius session exists (cookie-based, no auth header).
   * Used to detect if a user logged in on another subdomain after a local logout.
   * Returns true if server reports valid session, false otherwise (including network errors).
   */
  async isSessionValid(): Promise<boolean> {
    const url = `${environment.sirius_url}/auth/session/check`;
    try {
      const response = await firstValueFrom(
        this.http
          .get<{ valid: boolean; reason?: string }>(url, {
            withCredentials: true,
          })
          .pipe(timeout(5000), take(1))
      );
      return response.valid === true;
    } catch {
      return false;
    }
  }

  /**
   * Clear the recently logged out timestamp. Called when user explicitly clicks "Log in".
   * This allows the normal auth flow to proceed.
   */
  clearLogoutFlag(): void {
    this.log(
      LOG_INFO,
      'Clearing recently logged out timestamp - user initiated login'
    );
    localStorage.removeItem(RECENTLY_LOGGED_OUT_KEY);
    this.isLoggingOut = false; // Also reset in-memory flag
  }

  /**
   * Record the logout timestamp. Persists across page reloads.
   * MUST be called BEFORE any redirects during logout.
   */
  private setLogoutFlag(): void {
    this.log(LOG_INFO, 'Setting recently logged out timestamp');
    localStorage.setItem(RECENTLY_LOGGED_OUT_KEY, Date.now().toString());
  }

  async broadcastLogout() {
    // Prevent duplicate logout broadcasts and mark this tab as logging out
    if (this.isLoggingOut) {
      this.log(
        LOG_INFO,
        'Logout already in progress, skipping duplicate broadcast'
      );
      return;
    }

    this.isLoggingOut = true;

    // CRITICAL: Set persistent flag FIRST before any redirects
    // This ensures the flag survives page reload and blocks checkAuth()
    this.setLogoutFlag();

    this.log(LOG_INFO, 'Broadcasting logout to tabs');

    // Clear leader election and SSO state before broadcasting
    localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);
    localStorage.removeItem(`${APP_NAMESPACE}_returning_from_sso`);
    localStorage.removeItem(this.leaderKey);
    sessionStorage.removeItem('auth_return_url');

    // Relinquish leadership
    this.relinquishLeader();

    this.authChannel.postMessage({
      type: 'LOGOUT_INIT',
      tabId: this.getTabId(),
      timestamp: Date.now(),
    });
    // Wait briefly to ensure other tabs receive the message before this tab redirects
    await new Promise((resolve) => {
      setTimeout(resolve, 100);
    });
  }

  private log(level: string, message: string, data?: any) {
    if (environment.debug) {
      switch (level) {
        case LOG_ERROR:
          // eslint-disable-next-line no-console
          console.error(`[AuthSyncService] ${message}`, data || '');
          break;
        case LOG_WARN:
          // eslint-disable-next-line no-console
          console.warn(`[AuthSyncService] ${message}`, data || '');
          break;
        case LOG_INFO:
          // eslint-disable-next-line no-console
          console.log(`[AuthSyncService] ${message}`, data || '');
      }
    }
  }

  isRefreshing = false;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private store: Store,
    private authService: PlAuthService,
    private http: HttpClient,
    private impersonationService: PlImpersonationService
  ) {
    this.log(LOG_INFO, '[TAB_INIT] AuthSyncService constructor called');

    this.store.select(selectAuthState).subscribe((authState) => {
      this.log(LOG_INFO, 'Auth store state updated:', authState);
    });

    this.authChannel = new BroadcastChannel('pl_auth_channel');
    this.log(LOG_INFO, `BroadcastChannel ${this.authChannel.name} created`);

    // Ensure this tab has a unique ID (must happen before any operations)
    this.initWorker();
    this.setupChannelListeners();
    this.detectAndHandleDuplicateTab(); // Detect duplicate tabs before leader election
    this.electLeader();

    window.addEventListener('storage', this.handleLeaderChange.bind(this));
    window.addEventListener('beforeunload', this.cleanupOnUnload.bind(this));

    // Check if this tab is returning from SSO
    const returningFromSSO = localStorage.getItem(
      `${APP_NAMESPACE}_returning_from_sso`
    );
    if (returningFromSSO === 'true') {
      this.log(
        LOG_INFO,
        'Returning from SSO - skipping requestAuthState, will process fresh tokens from callback'
      );
    } else {
      this.requestAuthState(); // Request auth state when service initializes in new tab
    }
  }

  private initWorker() {
    this.authWorker = new Worker('/assets/workers/auth-worker.js', {
      type: 'module',
    });

    this.log(LOG_INFO, 'Worker initialized and ready');

    this.authWorker.onmessage = (event) => {
      const { type, timestamp, error } = event.data;

      switch (type) {
        case 'HEARTBEAT':
          this.maintainLeadership(timestamp);
          break;

        case 'TOKEN_REFRESH_NEEDED':
          // Don't start token refresh if we're logging out
          if (this.isLoggingOut) {
            this.log(
              LOG_INFO,
              'Tab is logging out - ignoring TOKEN_REFRESH_NEEDED'
            );
            break;
          }

          this.log(LOG_INFO, 'Token refresh needed');
          if (this.isLeader && !this.isRefreshing) {
            this.log(LOG_INFO, 'Leader tab will refresh token');
            this.handleTokenRefresh();
          }
          break;

        case 'AUTH_ERROR':
          this.log(LOG_ERROR, 'Auth error received from worker:', error);
          this.store.dispatch(PLAuthError({ error }));
          break;

        default:
          this.log(LOG_WARN, `Unhandled worker message type: ${type}`);
      }
    };
  }

  private handleLeaderChange(event: StorageEvent) {
    if (event.key === this.leaderKey) {
      if (event.newValue) {
        const { tabId, timestamp } = JSON.parse(event.newValue);

        // Ignore updates from the current leader
        if (this.isLeader && this.getTabId() === tabId) {
          return;
        }

        // ignore if the leader is still active
        const now = Date.now();
        if (now - timestamp <= 5000) {
          return;
        }

        this.log(
          LOG_WARN,
          `Leader tab ${tabId} is inactive. This tab is taking over.`
        );
        this.electLeader();
      } else {
        this.log(LOG_WARN, 'Leader key was removed. Electing new leader.');
        const randomDelay = Math.floor(Math.random() * 1000); // 0-100ms delay
        setTimeout(() => this.electLeader(), randomDelay);
      }
    }
  }

  private becomeLeader() {
    this.isLeader = true;

    localStorage.setItem(
      this.leaderKey,
      JSON.stringify({ tabId: this.getTabId(), timestamp: Date.now() })
    );

    this.authWorker?.postMessage({ type: 'BECOME_LEADER' });
  }

  private relinquishLeader() {
    this.isLeader = false;
    this.log(LOG_INFO, 'Relinquishing leadership');
    this.authWorker?.postMessage({ type: 'RELINQUISH_LEADER' });
  }

  /**
   * Elects a leader tab with jitter to reduce race conditions.
   * Adds a random delay (0-200ms) to prevent simultaneous leader election
   * when multiple tabs initialize at the same time (e.g., dev server HMR).
   */
  private async electLeader() {
    // Don't become leader if on logged-out page - no tokens, nothing to lead with
    if (window.location.pathname === '/auth/logged-out') {
      this.log(
        LOG_INFO,
        '[LEADER ELECTION] On logged-out page, skipping leader election'
      );
      return;
    }

    // Add random jitter (0-200ms) to reduce race conditions during simultaneous initialization
    const jitter = Math.random() * 200;
    this.log(
      LOG_INFO,
      `[LEADER ELECTION] Adding ${Math.floor(
        jitter
      )}ms jitter to reduce race conditions`
    );
    await new Promise((resolve) => {
      setTimeout(resolve, jitter);
    });

    const existingLeader = localStorage.getItem(this.leaderKey);
    const now = Date.now();

    if (!existingLeader || now - JSON.parse(existingLeader).timestamp > 5000) {
      // Check if previous leader is at SSO - if so, wait for them
      const leaderAtSSOData = localStorage.getItem(
        `${APP_NAMESPACE}_leader_at_sso`
      );
      if (leaderAtSSOData) {
        try {
          const ssoInfo = JSON.parse(leaderAtSSOData);
          const waitTime = now - ssoInfo.timestamp;

          if (waitTime < 10000) {
            // 10 seconds
            this.log(
              LOG_INFO,
              `Previous leader (${ssoInfo.tabId}) at SSO (${waitTime}ms ago) - entering passive wait mode`
            );
            this.enterPassiveWaitMode(ssoInfo.timestamp);
            return; // Don't become leader
          }
          this.log(
            LOG_WARN,
            `Leader SSO timed out (${waitTime}ms) - cleaning up and taking over leadership`
          );
          localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);
          localStorage.removeItem(`${APP_NAMESPACE}_returning_from_sso`);
          // Fall through to become leader
        } catch (e) {
          // NOTE: Useful for debugging - but don't enable in prod!
          // this.log(LOG_ERROR, 'Failed to parse leader_at_sso data:', e);
          localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);
        }
      }

      // No leader or leader timed out (and not at SSO)
      if (!existingLeader) {
        this.log(LOG_INFO, 'No leader exists, this tab becomes the leader');
      } else {
        const { tabId, timestamp } = JSON.parse(existingLeader);
        this.log(
          LOG_WARN,
          `Leader tab ${tabId} is inactive (last active ${
            now - timestamp
          }ms ago). This tab is taking over.`
        );
      }
      this.becomeLeader();
    } else {
      const { tabId, timestamp } = JSON.parse(existingLeader);

      // Check if we ARE the existing leader (returning from SSO, for example)
      if (tabId === this.getTabId()) {
        this.log(
          LOG_INFO,
          `We are the existing leader (tab ${tabId}) - reclaiming leadership`
        );
        this.becomeLeader();
      } else {
        this.log(
          LOG_INFO,
          `Leader exists: Tab ${tabId} is the current leader, last active ${
            now - timestamp
          }ms ago.`
        );
      }
    }
  }

  /**
   * Enter passive wait mode when previous leader is at SSO.
   * This tab will not try to refresh tokens or become leader.
   * It will wait for the REFRESH_DONE broadcast from the returning leader.
   */
  private enterPassiveWaitMode(ssoStartTimestamp: number) {
    this.log(LOG_INFO, 'Entering passive wait mode - leader at SSO');

    // Stop token monitoring to prevent this tab from trying to refresh
    if (this.authWorker) {
      this.authWorker.postMessage({ type: 'STOP_MONITORING' });
      this.log(LOG_INFO, 'Stopped token monitoring during passive wait');
    }

    // Clear any existing timeout to prevent multiple timeouts from piling up
    if (this.passiveWaitTimeout) {
      clearTimeout(this.passiveWaitTimeout);
      this.passiveWaitTimeout = null;
    }

    // Set a timeout to handle case where leader never returns
    const timeoutDuration = 10000; // 10 seconds

    this.passiveWaitTimeout = setTimeout(() => {
      const stillWaiting = localStorage.getItem(
        `${APP_NAMESPACE}_leader_at_sso`
      );
      if (stillWaiting) {
        const waitTime = Date.now() - ssoStartTimestamp;
        this.log(
          LOG_WARN,
          `Leader SSO timeout after ${waitTime}ms - attempting to claim SSO handling`
        );

        // CRITICAL: Use distributed lock to prevent multiple tabs from calling authorize()
        // when they all timeout simultaneously
        const tabId = this.getTabId();
        const myClaimValue = JSON.stringify({
          tabId,
          timestamp: Date.now(),
        });

        // Try to claim SSO handling (overwrite any stale claim)
        localStorage.setItem(`${APP_NAMESPACE}_leader_at_sso`, myClaimValue);
        localStorage.setItem(`${APP_NAMESPACE}_returning_from_sso`, 'true');

        // Yield to event loop to allow other tabs' writes to propagate
        // This implements proper compare-and-swap: write, wait, verify
        setTimeout(() => {
          // Don't re-auth if logout started
          if (this.isLoggingOut) {
            this.log(LOG_INFO, 'Tab is logging out - aborting SSO claim');
            return;
          }

          const currentValue = localStorage.getItem(
            `${APP_NAMESPACE}_leader_at_sso`
          );

          // Only proceed if our exact claim value is still present
          // If another tab overwrote us, currentValue !== myClaimValue
          if (currentValue === myClaimValue) {
            this.log(
              LOG_INFO,
              'Successfully claimed SSO handling after timeout (CAS verified), initiating SSO'
            );
            this.store.dispatch(PLAuthError({ error: 'Leader SSO timeout' }));
            this.oidcSecurityService.authorize();
          } else {
            this.log(
              LOG_WARN,
              'Another tab claimed SSO during timeout - our claim was overwritten, backing off'
            );
          }
        }, 50);
      } else {
        this.log(
          LOG_INFO,
          'Leader returned from SSO successfully - passive wait complete'
        );
      }
    }, timeoutDuration);

    this.log(
      LOG_INFO,
      `Set ${timeoutDuration}ms timeout for leader SSO return`
    );
  }

  private maintainLeadership(timestamp: number) {
    if (this.isLeader) {
      localStorage.setItem(
        this.leaderKey,
        JSON.stringify({ tabId: this.getTabId(), timestamp })
      );
    }
  }

  private cleanupOnUnload() {
    // Check if we're going to SSO - if so, preserve leadership state for return
    const atSSO = localStorage.getItem(`${APP_NAMESPACE}_leader_at_sso`);

    if (atSSO) {
      this.log(
        LOG_INFO,
        'Tab redirecting to SSO - preserving leader state for return'
      );
      // Don't remove leader key, don't call relinquishLeader - maintain leadership through redirect
    } else {
      if (this.isLeader) {
        this.log(LOG_INFO, 'Tab is closing. Removing leader key.');
        localStorage.removeItem(this.leaderKey);
      }
      this.relinquishLeader();
    }
  }

  private hasOidcState(
    message: AuthChannelMessage
  ): message is AuthStateMessage {
    return message.type === 'AUTH_STATE' && 'oidcState' in message;
  }

  /**
   * Check if this tab is actively processing an OAuth callback.
   *
   * CRITICAL: We check if the URL state matches sessionStorage authStateControl.
   * This distinguishes between:
   * 1. Processing OUR callback (state matches) - block broadcasts to prevent corruption
   * 2. Stuck on callback route (state mismatch/missing) - accept broadcasts for rescue
   *
   * If we're on the callback route and the state matches, we MUST reject broadcasts.
   * If state doesn't match, we're a stuck follower tab that NEEDS the broadcast.
   */
  private isProcessingOAuthCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const urlState = urlParams.get('state');

    // Not on callback route or no state param
    if (window.location.pathname !== '/auth/callback' || !urlState) {
      return false;
    }

    // Check if the URL state matches our sessionStorage (meaning it's OUR callback)
    try {
      const oidcState = sessionStorage.getItem(environment.oidc_client_id);
      if (!oidcState) {
        // No session storage - we're stuck, need rescue broadcast
        return false;
      }

      const parsed = JSON.parse(oidcState);
      const stateMatch = parsed.authStateControl === urlState;

      // Only block broadcasts if state matches (processing OUR callback)
      // If state doesn't match, we're stuck with corrupted state and NEED the broadcast
      return stateMatch;
    } catch (e) {
      // Error parsing - assume we're stuck and need rescue
      return false;
    }
  }

  private setupChannelListeners() {
    this.log(LOG_INFO, 'Setting up channel listeners');

    this.authChannel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
      switch (event.data.type) {
        case 'REQUEST_AUTH_STATE':
          // Don't respond to our own request
          if (event.data.tabId === this.getTabId()) {
            this.log(LOG_INFO, 'Ignoring our own REQUEST_AUTH_STATE');
            break;
          }

          // Don't broadcast auth state if we're logging out
          if (this.isLoggingOut) {
            this.log(
              LOG_INFO,
              'Tab is logging out - ignoring REQUEST_AUTH_STATE to prevent broadcasting stale auth'
            );
            break;
          }

          this.oidcSecurityService
            .getAccessToken()
            .pipe(take(1))
            .subscribe((token) => {
              if (token) {
                this.oidcSecurityService
                  .getUserData()
                  .pipe(take(1))
                  .subscribe((userData) => {
                    const oidcState = sessionStorage.getItem(
                      environment.oidc_client_id
                    );
                    if (oidcState) {
                      this.authChannel.postMessage({
                        type: 'AUTH_STATE',
                        tabId: this.getTabId(),
                        timestamp: Date.now(),
                        token,
                        userData,
                        oidcState,
                      } as AuthStateMessage);
                    } else {
                      this.log(
                        LOG_WARN,
                        'No OIDC state found in session storage'
                      );
                    }
                  });
              } else {
                this.log(
                  LOG_WARN,
                  'No access token available to send AUTH_STATE'
                );
              }
            });
          break;

        case 'AUTH_STATE':
          if (this.hasOidcState(event.data)) {
            // Don't process our own broadcast
            if (event.data.tabId === this.getTabId()) {
              this.log(LOG_INFO, 'Ignoring our own AUTH_STATE broadcast');
              return;
            }

            // CRITICAL: Don't process auth state if we've logged out or are on logged-out page
            // Prevents race condition where logout happens while another tab broadcasts fresh auth state
            if (
              this.isLoggingOut ||
              this.isRecentlyLoggedOut() ||
              window.location.pathname === '/auth/logged-out'
            ) {
              this.log(
                LOG_INFO,
                'Tab is logged out - ignoring AUTH_STATE broadcast to prevent re-login'
              );
              return;
            }

            // CRITICAL: Don't process broadcast if we're actively processing our own OAuth callback
            // This includes:
            // 1. Being on callback route with OAuth parameters
            // 2. Having OAuth flow in progress (authStateControl/codeVerifier present)
            // MUST check BEFORE writing to sessionStorage to avoid overwriting our own OAuth state
            if (this.isProcessingOAuthCallback()) {
              this.log(
                LOG_INFO,
                'Actively processing OAuth callback - ignoring AUTH_STATE broadcast (not writing to sessionStorage)'
              );
              // DON'T write to sessionStorage - would overwrite our own OAuth state
              // DON'T signal authStateReceived - let this tab complete its own OAuth flow
              return;
            }

            this.oidcSecurityService
              .getAccessToken()
              .pipe(take(1))
              .subscribe((currentToken) => {
                if (!currentToken) {
                  const data = event.data as AuthStateMessage;

                  sessionStorage.setItem(
                    environment.oidc_client_id,
                    data.oidcState
                  );

                  // First dispatch to set the token in store (so HTTP interceptor can use it)
                  // eslint-disable-next-line ngrx/avoid-dispatching-multiple-actions-sequentially
                  this.store.dispatch(
                    PLAuthSuccess({
                      token: data.token,
                      user: data.userData,
                    })
                  );

                  this.log(LOG_INFO, 'Got auth state from another tab');
                  // Signal immediately that auth state was received (before async group fetch)
                  // This prevents app.component from timing out and calling checkAuth()
                  this.authStateReceived$.next(true);

                  // Now fetch user groups in background - token is available in store for HTTP interceptor
                  // The auth has already been validated by the tab that sent this message
                  const oidcStateAsJSON = JSON.parse(
                    data.oidcState
                  ) as OIDCState;
                  const userData = oidcStateAsJSON.userData;

                  this.authService.getUser().subscribe({
                    next: (result) => {
                      // Transform raw OIDC userData to application User format
                      const user = {
                        uuid: userData.auth_db_user_uuid,
                        username: userData.auth_db_user_username,
                        email: userData.email,
                        display_name: userData.name,
                        first_name: userData.given_name,
                        last_name: userData.family_name,
                        is_active: userData.email_verified,
                        is_staff: userData.role !== 'provider',
                      };

                      // Re-dispatch with enriched user including groups
                      // eslint-disable-next-line ngrx/avoid-dispatching-multiple-actions-sequentially
                      this.store.dispatch(
                        PLAuthSuccess({
                          user: { ...user, groups: result.groups },
                          token: data.token,
                        })
                      );

                      this.log(LOG_INFO, 'User groups fetched and merged');
                      this.startSessionPolling();
                    },
                    error: (userError) => {
                      this.log(
                        LOG_ERROR,
                        '[AUTH_STATE] Error fetching user groups:',
                        userError
                      );
                      // Auth still works, just without groups enrichment
                      // Update store anyway with basic user data
                      // Transform raw OIDC userData to application User format
                      const user = {
                        uuid: userData.auth_db_user_uuid,
                        username: userData.auth_db_user_username,
                        email: userData.email,
                        display_name: userData.name,
                        first_name: userData.given_name,
                        last_name: userData.family_name,
                        is_active: userData.email_verified,
                        is_staff: userData.role !== 'provider',
                      };

                      // Re-dispatch with user data but without groups
                      // eslint-disable-next-line ngrx/avoid-dispatching-multiple-actions-sequentially
                      this.store.dispatch(
                        PLAuthSuccess({
                          user: { ...user, groups: [] },
                          token: data.token,
                        })
                      );

                      this.log(
                        LOG_INFO,
                        '[AUTH_STATE] User data updated without groups due to fetch error'
                      );
                      this.startSessionPolling();
                    },
                  });
                }
              });
          } else {
            this.log(
              LOG_WARN,
              'Received message is not AUTH_STATE',
              event.data
            );
          }
          break;

        case 'REFRESH_INIT':
          // Don't process our own broadcast
          if (event.data.tabId === this.getTabId()) {
            this.log(LOG_INFO, 'Ignoring our own REFRESH_INIT broadcast');
            break;
          }

          this.log(
            LOG_INFO,
            'Invoked REFRESH_INIT - another tab is handling the refresh'
          );
          this.isRefreshing = true;
          break;

        case 'LOGOUT_INIT':
          // Don't process our own logout broadcast
          if (event.data.tabId === this.getTabId()) {
            this.log(LOG_INFO, 'Ignoring our own LOGOUT_INIT broadcast');
            break;
          }

          this.log(LOG_INFO, `Processing logout from tab ${event.data.tabId}`);
          this.handleLogoutMessage(event.data);
          break;

        case 'REFRESH_DONE': {
          // Don't process our own broadcast
          if (event.data.tabId === this.getTabId()) {
            this.log(LOG_INFO, 'Ignoring our own REFRESH_DONE broadcast');
            this.isRefreshing = false; // Still clear the flag
            break;
          }

          this.log(
            LOG_INFO,
            'Invoked REFRESH_DONE - refresh completed by another tab, syncing'
          );
          this.isRefreshing = false;

          // Clear passive wait timeout since leader returned successfully
          if (this.passiveWaitTimeout) {
            clearTimeout(this.passiveWaitTimeout);
            this.passiveWaitTimeout = null;
            this.log(
              LOG_INFO,
              'Cleared passive wait timeout - leader returned'
            );
          }

          // CRITICAL: Don't process refresh if we've logged out or are on logged-out page
          // Prevents race condition where logout happens while tab is at Sirius refreshing token
          if (
            this.isLoggingOut ||
            this.isRecentlyLoggedOut() ||
            window.location.pathname === '/auth/logged-out'
          ) {
            this.log(
              LOG_INFO,
              'Tab is logged out - ignoring REFRESH_DONE broadcast to prevent re-login'
            );
            return;
          }

          // CRITICAL: Don't process broadcast if we're actively processing our own OAuth callback
          if (this.isProcessingOAuthCallback()) {
            this.log(
              LOG_INFO,
              'Actively processing OAuth callback - ignoring REFRESH_DONE broadcast'
            );
            return;
          }

          const { token, oidcState, userData } = event.data;
          if (token && oidcState && userData) {
            // Success case - leader returned with fresh tokens
            sessionStorage.setItem(environment.oidc_client_id, oidcState);

            // First dispatch to set the token in store (so HTTP interceptor can use it)
            // eslint-disable-next-line ngrx/avoid-dispatching-multiple-actions-sequentially
            this.store.dispatch(PLAuthSuccess({ token, user: userData }));

            this.log(
              LOG_INFO,
              'Successfully synced refreshed auth from leader tab'
            );

            // CRITICAL: Check if logout started while we were processing
            // This prevents making API calls after logout
            if (this.isLoggingOut) {
              this.log(
                LOG_INFO,
                '[REFRESH_DONE] Tab is logging out - aborting enrichment'
              );
              return;
            }

            // CRITICAL: Fetch groups from Django to enrich user data
            // This mirrors what AUTH_STATE handler does (lines 620-642) and app.component.ts (line 450)
            // Token is now available in store for HTTP interceptor
            this.authService.getUser().subscribe({
              next: (result) => {
                // Transform raw OIDC userData to application User format
                const user = {
                  uuid: userData.auth_db_user_uuid,
                  username: userData.auth_db_user_username,
                  email: userData.email,
                  display_name: userData.name,
                  first_name: userData.given_name,
                  last_name: userData.family_name,
                  is_active: userData.email_verified,
                  is_staff: userData.role !== 'provider',
                };

                // Re-dispatch with enriched user including groups
                // eslint-disable-next-line ngrx/avoid-dispatching-multiple-actions-sequentially
                this.store.dispatch(
                  PLAuthSuccess({
                    user: { ...user, groups: result.groups },
                    token,
                  })
                );

                this.log(
                  LOG_INFO,
                  '[REFRESH_DONE] User groups fetched and merged'
                );
                this.startSessionPolling();
              },
              error: (userError) => {
                this.log(
                  LOG_ERROR,
                  '[REFRESH_DONE] Error fetching user groups:',
                  userError
                );
                // Auth still works, just without groups enrichment
                // Update store anyway with basic user data
                // Transform raw OIDC userData to application User format
                const user = {
                  uuid: userData.auth_db_user_uuid,
                  username: userData.auth_db_user_username,
                  email: userData.email,
                  display_name: userData.name,
                  first_name: userData.given_name,
                  last_name: userData.family_name,
                  is_active: userData.email_verified,
                  is_staff: userData.role !== 'provider',
                };

                // Re-dispatch with user data but without groups
                // eslint-disable-next-line ngrx/avoid-dispatching-multiple-actions-sequentially
                this.store.dispatch(
                  PLAuthSuccess({
                    user: { ...user, groups: [] },
                    token,
                  })
                );

                this.log(
                  LOG_INFO,
                  '[REFRESH_DONE] User data updated without groups due to fetch error'
                );
                this.startSessionPolling();
              },
            });
          } else {
            // Error case - leader failed during SSO
            this.log(
              LOG_WARN,
              'Leader failed during SSO, checking if another tab is handling it'
            );

            // CRITICAL: Use dist lock to prevent race when leader fails
            // ...if this occurred multiple followers could all see the failure broadcast simultaneously
            const tabId = this.getTabId();
            const myClaimValue = JSON.stringify({
              tabId,
              timestamp: Date.now(),
            });

            const existingClaim = localStorage.getItem(
              `${APP_NAMESPACE}_leader_at_sso`
            );

            if (!existingClaim) {
              // No one is handling it yet, try to claim it...
              localStorage.setItem(
                `${APP_NAMESPACE}_leader_at_sso`,
                myClaimValue
              );
              localStorage.setItem(
                `${APP_NAMESPACE}_returning_from_sso`,
                'true'
              );

              // Yield to event loop to allow other tabs' writes to propagate
              setTimeout(() => {
                // Don't re-auth if logout started
                if (this.isLoggingOut) {
                  this.log(LOG_INFO, 'Tab is logging out - aborting SSO claim');
                  return;
                }

                const currentValue = localStorage.getItem(
                  `${APP_NAMESPACE}_leader_at_sso`
                );

                // Only proceed if our exact claim value is still present
                if (currentValue === myClaimValue) {
                  this.log(
                    LOG_INFO,
                    'Successfully claimed SSO after leader failure (CAS verified), initiating SSO'
                  );
                  this.store.dispatch(
                    PLAuthError({ error: 'Leader SSO failed, retrying' })
                  );
                  this.oidcSecurityService.authorize();
                } else {
                  // Another tab overwrote our claim, enter passive wait
                  try {
                    const winner = JSON.parse(currentValue || '{}');
                    this.log(
                      LOG_INFO,
                      `Another tab (${winner.tabId}) claimed SSO - our claim was overwritten, entering passive wait`
                    );
                    this.enterPassiveWaitMode(winner.timestamp || Date.now());
                  } catch (e) {
                    this.log(
                      LOG_ERROR,
                      'Error parsing winner claim, entering passive wait anyway'
                    );
                    this.enterPassiveWaitMode(Date.now());
                  }
                }
              }, 50);
            } else {
              // Another follower is already handling it, wait for them
              try {
                const ssoInfo = JSON.parse(existingClaim);
                this.log(
                  LOG_INFO,
                  `Another tab (${ssoInfo.tabId}) is handling SSO - entering passive wait`
                );
                this.enterPassiveWaitMode(ssoInfo.timestamp);
              } catch (e) {
                this.log(LOG_ERROR, 'Error parsing existing claim');
              }
            }
          }
          break;
        }

        case 'TAB_ID_ANNOUNCEMENT': {
          const announcedTabId = event.data.tabId;
          const myTabId = this.getTabId();

          // If another tab announces the same ID as ours, we have a conflict
          if (announcedTabId === myTabId) {
            this.log(
              LOG_WARN,
              `[DUPLICATE TAB] Conflict detected! Another tab has ID: ${announcedTabId}`
            );

            // Send conflict notification back to the announcing tab
            this.authChannel.postMessage({
              type: 'TAB_ID_CONFLICT',
              tabId: myTabId,
              conflictingTabId: announcedTabId,
              timestamp: Date.now(),
            } as TabIdConflictMessage);

            // Regenerate our own ID to resolve the conflict
            this.regenerateTabId();
          }
          break;
        }

        case 'TAB_ID_CONFLICT': {
          const conflictingTabId = event.data.conflictingTabId;
          const myTabId = this.getTabId();

          // If we receive a conflict message about our ID, regenerate
          if (conflictingTabId === myTabId) {
            this.log(
              LOG_WARN,
              `[DUPLICATE TAB] Received conflict notification for our ID: ${myTabId}`
            );
            this.regenerateTabId();
          }
          break;
        }
      }
    };
  }

  private requestAuthState() {
    this.log(LOG_INFO, 'Requesting auth state');

    // Reset the auth state received flag before requesting
    this.authStateReceived$.next(false);

    this.authChannel.postMessage({
      type: 'REQUEST_AUTH_STATE',
      tabId: this.getTabId(),
      timestamp: Date.now(),
    });
  }

  private getTabId(): string {
    const tabIdKey = `${APP_NAMESPACE}_tab_id`;
    const existing = sessionStorage.getItem(tabIdKey);

    // If tab ID exists in storage, cache it and return
    if (existing) {
      this.tabIdCache = existing;
      return existing;
    }

    // If missing from storage but we have cache, restore it
    if (!existing && this.tabIdCache) {
      sessionStorage.setItem(tabIdKey, this.tabIdCache);
      return this.tabIdCache;
    }

    // No storage, no cache - create new ID
    const newId = crypto.randomUUID();
    this.log(LOG_INFO, `[TAB_INIT] Creating new tab ID: ${newId}`);
    sessionStorage.setItem(tabIdKey, newId);
    this.tabIdCache = newId;
    return newId;
  }

  /**
   * Detects and handles duplicate tab IDs that occur when a user duplicates a tab.
   * When a tab is duplicated (right-click → Duplicate), sessionStorage is copied,
   * including the tab ID. This method announces the tab ID and listens for conflicts.
   *
   * CRITICAL: This must be called AFTER setupChannelListeners() but BEFORE electLeader()
   * to ensure duplicate tabs don't interfere with leader election.
   */
  private detectAndHandleDuplicateTab(): void {
    const currentTabId = this.getTabId();

    this.log(
      LOG_INFO,
      `[DUPLICATE TAB DETECTION] Announcing tab ID: ${currentTabId}`
    );

    // Announce our tab ID to other tabs
    this.authChannel.postMessage({
      type: 'TAB_ID_ANNOUNCEMENT',
      tabId: currentTabId,
      timestamp: Date.now(),
    } as TabIdAnnouncementMessage);
  }

  /**
   * Generates a new tab ID when a duplicate is detected.
   * Called when another tab announces it has the same ID as this tab.
   */
  private regenerateTabId(): void {
    const tabIdKey = `${APP_NAMESPACE}_tab_id`;
    const oldTabId = this.getTabId();
    const newTabId = crypto.randomUUID();

    sessionStorage.setItem(tabIdKey, newTabId);
    this.tabIdCache = newTabId;

    this.log(
      LOG_WARN,
      `[DUPLICATE TAB] Detected duplicate tab ID (${oldTabId}), generated new ID: ${newTabId}`
    );

    // Re-announce with new ID
    this.authChannel.postMessage({
      type: 'TAB_ID_ANNOUNCEMENT',
      tabId: newTabId,
      timestamp: Date.now(),
    } as TabIdAnnouncementMessage);
  }

  notifyRefreshStart() {
    this.authChannel.postMessage({
      type: 'REFRESH_INIT',
      tabId: this.getTabId(),
      timestamp: Date.now(),
    });
  }

  notifyRefreshDone(token: string, oidcState: string, userData: any) {
    this.authChannel.postMessage({
      type: 'REFRESH_DONE',
      tabId: this.getTabId(),
      timestamp: Date.now(),
      token,
      oidcState,
      userData,
    });
  }

  /**
   * Called after returning from SSO re-authentication.
   * Broadcasts fresh tokens to all tabs regardless of leader status.
   * This handles the case where this tab lost leadership during SSO redirect
   * but now has fresh tokens that other tabs need.
   */
  handlePostSSOTokenBroadcast() {
    this.log(LOG_INFO, 'Handling post-SSO token broadcast');

    // Clear any previous error state from the store
    // (we dispatched PLAuthError before calling authorize)
    this.isRefreshing = false;

    this.oidcSecurityService
      .getAccessToken()
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          this.log(LOG_INFO, 'Post-SSO: Got access token:', {
            hasToken: !!token,
            tokenLength: token?.length,
            tokenPrefix: token?.substring(0, 20),
          });

          if (token) {
            this.oidcSecurityService
              .getUserData()
              .pipe(take(1))
              .subscribe({
                next: (userData) => {
                  this.log(LOG_INFO, 'Post-SSO: Got user data:', {
                    hasUserData: !!userData,
                    rawUserId: userData?.auth_db_user_uuid,
                  });

                  // Transform userData to match the expected User structure
                  // (same mapping as in app.component.ts handleAuthenticated)
                  const user = {
                    uuid: userData.auth_db_user_uuid,
                    username: userData.auth_db_user_username,
                    email: userData.email,
                    display_name: userData.name,
                    first_name: userData.given_name,
                    last_name: userData.family_name,
                    is_active: true,
                    is_staff: userData.role !== 'provider',
                  };

                  this.log(LOG_INFO, 'Post-SSO: Mapped user:', {
                    uuid: user.uuid,
                    email: user.email,
                  });

                  const oidcState = sessionStorage.getItem(
                    environment.oidc_client_id
                  );
                  this.log(LOG_INFO, 'Broadcasting fresh tokens from post-SSO');

                  // Dispatch success to clear error state
                  this.log(
                    LOG_INFO,
                    'About to dispatch PLAuthSuccess with token and user'
                  );
                  this.store.dispatch(PLAuthSuccess({ token, user }));
                  this.log(LOG_INFO, 'PLAuthSuccess dispatched');

                  // Check auth state immediately after dispatch
                  this.store
                    .select(selectAuthState)
                    .pipe(take(1))
                    .subscribe((state) => {
                      this.log(
                        LOG_INFO,
                        'Auth state immediately after PLAuthSuccess dispatch:',
                        state
                      );
                    });

                  // Broadcast RAW userData to all tabs (not transformed)
                  // Receiving tabs will transform it themselves
                  this.notifyRefreshDone(token, oidcState || '', userData);

                  // Clear SSO flags - we're back and have broadcast tokens
                  localStorage.removeItem(
                    `${APP_NAMESPACE}_returning_from_sso`
                  );
                  localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);
                  this.log(
                    LOG_INFO,
                    'SSO return complete - cleared flags, resuming normal operation'
                  );

                  if (this.authWorker) {
                    this.startTokenMonitoring(token);
                  }
                  this.startSessionPolling();
                },
                error: () => {
                  this.log(LOG_ERROR, 'Error getting user data post-SSO');

                  // Clear SSO flags to unblock other tabs
                  localStorage.removeItem(
                    `${APP_NAMESPACE}_returning_from_sso`
                  );
                  localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);

                  // Dispatch error to update auth state
                  this.store.dispatch(
                    PLAuthError({
                      error: 'Failed to retrieve user data after SSO',
                    })
                  );

                  // Notify other tabs that refresh failed
                  this.notifyRefreshDone('', '', '');
                },
              });
          } else {
            this.log(LOG_WARN, 'No access token found after SSO');

            // Clear SSO flags to unblock other tabs
            localStorage.removeItem(`${APP_NAMESPACE}_returning_from_sso`);
            localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);

            // Dispatch error to update auth state
            this.store.dispatch(
              PLAuthError({ error: 'No access token after SSO' })
            );

            // Notify other tabs that refresh failed
            this.notifyRefreshDone('', '', '');
          }
        },
        error: () => {
          this.log(LOG_ERROR, 'Error getting access token post-SSO');

          // Clear SSO flags to unblock other tabs
          localStorage.removeItem(`${APP_NAMESPACE}_returning_from_sso`);
          localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);

          // Dispatch error to update auth state
          this.store.dispatch(
            PLAuthError({ error: 'Failed to retrieve access token after SSO' })
          );

          // Notify other tabs that refresh failed
          this.notifyRefreshDone('', '', '');
        },
      });
  }

  startTokenMonitoring(token: string) {
    if (!this.authWorker) {
      this.log(LOG_WARN, 'No worker available for token monitoring');
      return;
    }

    this.oidcSecurityService
      .getConfiguration()
      .pipe(take(1))
      .subscribe((config) => {
        const refreshThreshold = config.renewTimeBeforeTokenExpiresInSeconds;

        this.log(
          LOG_INFO,
          'Starting token monitoring with refresh threshold:',
          refreshThreshold
        );
        this.authWorker.postMessage({
          type: 'START_TOKEN_MONITORING',
          data: {
            token,
            refreshThreshold,
          },
        });
      });
  }

  private cachedState: {
    oidcState?: string | null;
    refreshToken?: string | null;
  } = {};

  private preserveAuthState() {
    const oidcState = sessionStorage.getItem(environment.oidc_client_id);

    this.oidcSecurityService
      .getRefreshToken()
      .pipe(take(1))
      .subscribe((refreshToken) => {
        this.cachedState = {
          oidcState,
          refreshToken,
        };

        this.log(LOG_INFO, 'Auth state preserved:', this.cachedState);
      });
  }

  private restoreAuthState(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.cachedState.oidcState || !this.cachedState.refreshToken) {
        this.log(LOG_WARN, 'No cached state to restore');
        resolve(false);
        return;
      }

      try {
        // Restore OIDC session state
        sessionStorage.setItem(
          environment.oidc_client_id,
          this.cachedState.oidcState
        );

        // Update the auth config with restored refresh token
        this.oidcSecurityService
          .getConfiguration()
          .pipe(take(1))
          .subscribe({
            next: (config) => {
              if (config) {
                sessionStorage.setItem(
                  `${config.configId}_refresh_token`,
                  this.cachedState.refreshToken
                );
                this.log(LOG_INFO, 'Auth state restored successfully');
                resolve(true);
              } else {
                this.log(
                  LOG_WARN,
                  'Configuration not available for restoring state'
                );
                resolve(false);
              }
            },
            error: (error) => {
              this.log(LOG_ERROR, 'Error while restoring auth state:', error);
              resolve(false);
            },
          });
      } catch (e) {
        this.log(LOG_ERROR, 'Failed to restore auth state:', e);
        resolve(false);
      }
    });
  }

  private handleTokenRefresh(retryAttempt = 0) {
    // Don't proceed with token refresh if we're logging out
    if (this.isLoggingOut) {
      this.log(LOG_INFO, 'Tab is logging out - aborting token refresh');
      return;
    }

    if (this.isRefreshing && retryAttempt === 0) {
      this.log(LOG_INFO, 'Refresh already in progress, skipping');
      return;
    }

    // On first attempt, preserve current state
    if (retryAttempt === 0) {
      this.preserveAuthState();
    }

    this.log(LOG_INFO, `Starting token refresh attempt ${retryAttempt + 1}`);
    this.isRefreshing = true;
    this.notifyRefreshStart();

    const refreshStartTime = Date.now();

    // If state is missing but we have cached state, restore it
    this.oidcSecurityService.getRefreshToken().subscribe({
      next: (refreshToken) => {
        if (!refreshToken && this.cachedState.refreshToken) {
          this.log(LOG_WARN, 'No refresh token available, attempting restore');
          this.restoreAuthState().then((restored) => {
            if (!restored) {
              this.log(
                LOG_WARN,
                'Failed to restore auth state, proceeding with refresh'
              );
            }
          });
        }

        this.oidcSecurityService.forceRefreshSession().subscribe({
          next: (result) => {
            // NOTE: Should you need to inspect values here,
            // you can do so by checking for one or both of:
            // - `result?.accessToken`
            // - `this.oidcSecurityService.getRefreshToken()`
            // Then decoding them using the helper I've added
            // for inspecting tokens `this.decodeToken(token)`

            const token = result?.accessToken;
            if (token) {
              this.oidcSecurityService.getUserData().subscribe({
                next: (userData) => {
                  const oidcState = sessionStorage.getItem(
                    environment.oidc_client_id
                  );

                  this.isRefreshing = false;
                  this.cachedState = {};

                  // CRITICAL: Update store with new token BEFORE making any HTTP calls
                  // The old token has been revoked by Sirius during rotation, so any
                  // requests using the old token will fail with 401.
                  const user = {
                    uuid: userData.auth_db_user_uuid,
                    username: userData.auth_db_user_username,
                    email: userData.email,
                    display_name: userData.name,
                    first_name: userData.given_name,
                    last_name: userData.family_name,
                    is_active: userData.email_verified,
                    is_staff: userData.role !== 'provider',
                  };
                  this.store.dispatch(PLAuthSuccess({ token, user }));

                  this.notifyRefreshDone(token, oidcState, userData);

                  this.refreshSession(token).then(
                    () => {
                      this.log(
                        LOG_INFO,
                        'Session successfully refreshed with sirius'
                      );
                    },
                    (sessionError) => {
                      this.log(
                        LOG_WARN,
                        'Failed to refresh session with sirius',
                        sessionError
                      );
                    }
                  );

                  if (this.authWorker) {
                    this.startTokenMonitoring(token);
                  }
                },
                error: (error) => {
                  this.log(LOG_ERROR, 'Error retrieving user data', error);
                  this.handleRefreshError(retryAttempt, error, false);
                },
              });
            } else {
              this.log(
                LOG_ERROR,
                'Token refresh response did not include an access token'
              );
              this.handleRefreshError(
                retryAttempt,
                new Error('Missing access token'),
                false
              );
            }
          },
          error: (error) => {
            this.log(
              LOG_ERROR,
              `Refresh failed after ${Date.now() - refreshStartTime}ms`,
              error
            );

            // Check if error is due to auth server being down (502/503)
            // Note: angular-auth-oidc-client wraps HttpErrorResponse, hiding status codes
            // We default to re-authentication (safer than infinite retries)
            const isServerDown = this.isServerDownError(error);

            if (isServerDown) {
              // Server is down (502/503) - retry with backoff
              this.handleRefreshError(retryAttempt, error, true);
            } else {
              // Default: trigger re-authentication
              // This handles rotation chain exhaustion (400), invalid tokens, and unknown errors
              this.log(LOG_WARN, 'Token refresh failed, triggering re-auth');
              this.isRefreshing = false;
              this.cachedState = {};
              this.store.dispatch(
                PLAuthError({ error: 'Authentication refresh required' })
              );

              // Set SSO flags so other tabs know to wait for this tab to return
              localStorage.setItem(
                `${APP_NAMESPACE}_returning_from_sso`,
                'true'
              );
              localStorage.setItem(
                `${APP_NAMESPACE}_leader_at_sso`,
                JSON.stringify({
                  tabId: this.getTabId(),
                  timestamp: Date.now(),
                })
              );
              this.log(
                LOG_INFO,
                `Set SSO flags for tab ${this.getTabId()} - other tabs will wait`
              );

              this.oidcSecurityService.authorize();
            }
          },
        });
      },
      error: (error) => {
        this.log(LOG_ERROR, 'Error fetching refresh token', error);
        this.isRefreshing = false;
        this.handleRefreshError(retryAttempt, error, false);
      },
    });
  }

  private handleLogoutMessage(message: LogoutMessage) {
    this.log(LOG_INFO, `Received logout message from tab ${message.tabId}`);

    // Prevent processing duplicate logout messages
    if (this.isLoggingOut) {
      this.log(LOG_INFO, 'Already logging out, ignoring duplicate message');
      return;
    }

    this.isLoggingOut = true;

    // CRITICAL: Set persistent flag FIRST before any redirects
    // This ensures the flag survives page reload and blocks checkAuth()
    this.setLogoutFlag();

    this.stopSessionPolling();
    this.impersonationService.stop();
    this.oidcSecurityService.logoffLocal(); // Ensure that the OIDC client clears its storage!

    if (this.authWorker) {
      this.authWorker.postMessage({ type: 'STOP_TOKEN_MONITORING' });
    }

    this.cachedState = {};

    // Clear leader election and SSO state
    localStorage.removeItem(`${APP_NAMESPACE}_leader_at_sso`);
    localStorage.removeItem(`${APP_NAMESPACE}_returning_from_sso`);
    localStorage.removeItem(this.leaderKey);
    sessionStorage.removeItem('auth_return_url');

    // Relinquish leadership if we were leader
    this.relinquishLeader();

    // Notify store about logout
    this.store.dispatch(PLAuthError({ error: 'User logged out' }));

    // Redirect follower tabs to logged-out page
    // Do NOT call authorize() - that creates a race condition where
    // followers re-authenticate before the leader's logout completes on Sirius
    // The logged-out page has a manual "Log in" button which gives time for
    // the server-side logout to complete
    setTimeout(() => {
      window.location.href = '/auth/logged-out';
    }, 100);
  }

  /**
   * Determines if a refresh token error is due to server downtime (retryable)
   * vs. a permanent error requiring re-authentication (non-retryable).
   *
   * Rotation Chain Exhaustion (400 Bad Request):
   * - Backend returns 400 when refresh token rotation chain reaches max limit or TTL
   * - Should trigger re-authentication via authorize() for seamless SSO
   * - User's Sirius session (if still valid) will allow instant SSO
   *
   * Server Down (502/503):
   * - Transient infrastructure issues
   * - Should be retried with exponential backoff
   *
   * @param error The error from forceRefreshSession()
   * @returns true if error is retryable (server down), false if re-auth required
   */
  private isServerDownError(error: any): boolean {
    // angular-auth-oidc-client wraps HttpErrorResponse in Error objects,
    // hiding the status property. We must infer from available clues...
    // this is an absurd design decision from their devs - but don't even
    // get me started...

    if (
      error?.status === 502 ||
      error?.status === 503 ||
      error?.error?.status === 502 ||
      error?.error?.status === 503
    ) {
      return true;
    }

    const errorMsg = (error?.message || '').toLowerCase();

    if (
      errorMsg.includes('502') ||
      errorMsg.includes('503') ||
      errorMsg.includes('bad gateway') ||
      errorMsg.includes('service unavailable')
    ) {
      return true;
    }

    // HEURISTIC: "[object Object]" message observed consistently,
    // which is not unsurprising for the angular-auth-oidc-client library...
    // this strongly suggests it couldn't stringify
    // the error properly, which I've noticed in other places -
    // in manual testing, this is what we can expect when facing network/server errors (502/503)
    if (errorMsg === '[object object]') {
      return true; // Likely server down - retry
    }

    // While 502/503 most often fall into the above, for 400s - which we expect in this
    // error path more often due to expected rotation limits - because the 3rd party
    // library strips out all that would be good and useful - we're left largely blind.
    // As such, this safe default of false is here intentionally to catch our 400s
    // and to serve as a fail closed event for anything else (which should be exceedingly
    // or impossibly rare).
    // Default to FALSE (don't retry) to avoid infinite loops on 400 errors
    return false;
  }

  private handleRefreshError(
    retryAttempt: number,
    error: any,
    isRetryable: boolean
  ) {
    const maxRetries = isRetryable ? 5 : 3;
    const baseDelay = isRetryable ? 2000 : 1000;
    const backoffMs = Math.min(baseDelay * 2 ** retryAttempt, 30000); // Max 30s

    if (retryAttempt < maxRetries) {
      this.log(
        LOG_WARN,
        `Scheduling retry ${retryAttempt + 1} in ${backoffMs}ms ${
          isRetryable ? '(server down)' : ''
        }`
      );

      setTimeout(() => {
        this.handleTokenRefresh(retryAttempt + 1);
      }, backoffMs);
    } else {
      this.log(LOG_ERROR, 'Max retries reached, notifying error');

      // Clear retry state
      this.isRefreshing = false;
      this.store.dispatch(
        PLAuthError({
          error: isRetryable
            ? 'Auth server unavailable after max retries'
            : error || 'Max refresh retries exceeded',
        })
      );
      this.notifyRefreshDone('', '', '');

      // If this was a server down scenario, try to restart monitoring with existing token
      if (isRetryable) {
        // Schedule a final monitoring cycle
        this.oidcSecurityService
          .getAccessToken()
          .pipe(take(1))
          .subscribe({
            next: (token) => {
              if (token) {
                this.startTokenMonitoring(token);
              } else {
                this.log(
                  LOG_WARN,
                  'No token available for monitoring after server down'
                );
              }
            },
            error: (tokenError) => {
              this.log(
                LOG_ERROR,
                'Failed to get token during final monitoring: ',
                tokenError
              );
            },
          });
      } else {
        // For other errors, trigger re-auth
        this.oidcSecurityService.authorize();
      }
    }
  }

  /**
   * Refreshes the server-side session via the RefreshSession handler in sirius
   * This uses the /session/refresh endpoint to renew the session token
   * without requiring a full token refresh
   * @param token The access token to use for session refresh
   * @returns A promise that resolves when the session has been refreshed
   */
  private refreshSession(token: string): Promise<void> {
    const url = `${environment.sirius_url}/auth/session/refresh`;

    return firstValueFrom(
      this.http.post<{ message: string }>(
        url,
        {},
        {
          headers: {
            Authorization: `JWT ${token}`,
          },
          withCredentials: true,
        }
      )
    ).then(() => {});
  }

  /**
   * Starts polling the Sirius session check endpoint.
   * This enables proactive logout discovery for idle tabs.
   *
   * When a user logs out in another app (e.g., room), this app will discover
   * the logout within 30 seconds rather than waiting for a 401 on the next API call.
   *
   * This is a UX optimization - token validation at the Edge Gateway remains
   * the security enforcement mechanism.
   */
  startSessionPolling(): void {
    if (this.sessionPollingInterval || this.isLoggingOut) {
      this.log(
        LOG_INFO,
        'Session polling not started: already polling or logging out'
      );
      return;
    }

    this.log(
      LOG_INFO,
      `Starting session polling (interval: ${this.SESSION_POLL_INTERVAL_MS}ms)`
    );

    this.sessionPollingInterval = setInterval(() => {
      if (!this.isRefreshing) {
        this.checkSessionValidity();
      }
    }, this.SESSION_POLL_INTERVAL_MS);
  }

  /**
   * Stops session polling. Called during logout or service destruction.
   */
  stopSessionPolling(): void {
    if (this.sessionPollingInterval) {
      this.log(LOG_INFO, 'Stopping session polling');
      clearInterval(this.sessionPollingInterval);
      this.sessionPollingInterval = null;
    }
  }

  /**
   * Checks session validity with the Sirius session check endpoint.
   * If session is invalid (logged out in another app), triggers the same
   * logout flow as a 401 token revocation would.
   */
  private checkSessionValidity(): void {
    if (
      this.isLoggingOut ||
      this.isRecentlyLoggedOut() ||
      window.location.pathname === '/auth/logged-out'
    ) {
      return;
    }

    const url = `${environment.sirius_url}/auth/session/check`;

    this.http
      .get<{ valid: boolean; reason?: string }>(url, {
        withCredentials: true,
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (!response.valid) {
            // Guard against races:
            // logout may have started while request was in-flight
            // Token refresh may be in progress revoking the tokens used by this check call
            if (this.isLoggingOut || this.isRefreshing) {
              return;
            }

            this.log(
              LOG_WARN,
              `Session invalidated via polling: ${response.reason}`
            );

            this.stopSessionPolling();
            this.impersonationService.stop();
            this.oidcSecurityService.logoffLocal();

            this.broadcastLogout().then(() => {
              window.location.href = '/auth/logged-out';
            });
          }
        },
        error: (err) => {
          // Network error - don't logout, 401 on next API call still works
          this.log(LOG_WARN, 'Session check failed (network error)', err);
        },
      });
  }

  ngOnDestroy() {
    // Clear passive wait timeout to prevent orphaned timeout after destruction
    if (this.passiveWaitTimeout) {
      clearTimeout(this.passiveWaitTimeout);
      this.passiveWaitTimeout = null;
    }

    this.stopSessionPolling();
    this.authWorker?.terminate();
    this.authChannel.close();
    window.removeEventListener('storage', this.handleLeaderChange.bind(this));
    window.removeEventListener('beforeunload', this.cleanupOnUnload.bind(this));
  }
}
